import { RouteObject, useParams } from "react-router-dom";
import { Button, LoadingSkeleton } from "@narmi/design_system";
import { useUserConfiguration } from "../../hooks/useUserConfiguration";
import { useNavigate } from "react-router-dom";
import { PageContainer, TopBarPageContainer } from "../../components/PageContainer";
import { PermissionedRoutes } from "../../components/PermissionedRoutes";
import { TopBar } from "../../components/TopBar";
import { CSRF_TOKEN } from "../csrf";
import { useEffect, useState } from "react";
import { AdminApiSchema, StaffApiSchema } from "@apiUtils/index.ts";

type ServiceAccount = StaffApiSchema.components["schemas"]["ServiceAccount"];
type AccessToken = StaffApiSchema.components["schemas"]["ServiceAccountToken"];
type Group = AdminApiSchema.components["schemas"]["GroupResponse"];
type GroupListResponse = AdminApiSchema.components["schemas"]["PaginatedGroupResponseList"];

const getServiceAccount = async (uuid: string): Promise<ServiceAccount> => {
  const response = await fetch(`/api/service_accounts/${uuid}/`);
  return response.json();
};

const getAccessToken = async (uuid: string): Promise<AccessToken> => {
  const response = await fetch(`/api/service_account_tokens/${uuid}/`);
  return response.json();
};

const getRoles = async (userUuid: string): Promise<GroupListResponse> => {
  const response = await fetch(`/v2/admin/groups/?user=${userUuid}`);
  return await response.json();
};

export const STAFF_ROUTES: RouteObject[] = [
  {
    element: <PermissionedRoutes requiredPermissions={["backoffice.view_staff_user"]} />,
    children: [
      {
        index: true,
        async lazy() {
          const { StaffList } = await import("./staff_list/StaffList");
          return {
            Component() {
              const { permissions, roles } = useUserConfiguration();
              const navigate = useNavigate();

              return (
                <TopBarPageContainer
                  title="Staff"
                  action={
                    <>
                      {roles.narmi && (
                        <Button
                          size="xs"
                          kind="secondary"
                          onClick={() => {
                            navigate("/staff/add_staff/bulk");
                          }}
                          label="Bulk staff upload"
                        />
                      )}
                      {permissions.includes("backoffice.add_staff_user") && (
                        <Button
                          size="xs"
                          onClick={() => {
                            navigate("/staff/add_staff");
                          }}
                          label="Add staff member"
                        />
                      )}
                    </>
                  }
                >
                  <StaffList />
                </TopBarPageContainer>
              );
            },
          };
        },
      },
    ],
  },
  {
    element: <PermissionedRoutes requiredPermissions={["backoffice.add_staff_user"]} />,
    children: [
      {
        path: "add_staff",
        async lazy() {
          const { AddStaffContainer } = await import("./AddStaff");
          return {
            Component() {
              const navigate = useNavigate();
              return (
                <>
                  <TopBar title="Add staff" backIconOnClick={() => navigate("/staff")} />
                  <PageContainer className="margin--top--xl">
                    <AddStaffContainer />
                  </PageContainer>
                </>
              );
            },
          };
        },
      },
    ],
  },
  {
    element: <PermissionedRoutes requiredPermissions={["admin_api.view_serviceaccount"]} />,
    children: [
      {
        path: "service_accounts/:id/",
        async lazy() {
          const { ServiceAccountDetail } = await import(
            "./service_accounts/ServiceAccountDetail.tsx"
          );
          const { ServiceAccountDetailTopBar } = await import(
            "./service_accounts/ServiceAccountDetailTopBar.tsx"
          );
          return {
            Component() {
              const { id } = useParams();
              const [isLoading, setIsLoading] = useState(true);
              const [accessToken, setAccessToken] = useState<AccessToken | null>(null);

              const [userRoles, setUserRoles] = useState<Group[]>([]);
              const [serviceAccount, setServiceAccount] = useState<ServiceAccount>({
                uuid: "",
                username: "Service Account",
                access_token: "",
                user: "",
                ip_allowlist: [],
              });

              const setAccessTokenState = (newAccessTokenState: AccessToken | null) => {
                setAccessToken(newAccessTokenState);
                setServiceAccount({
                  ...serviceAccount,
                  access_token: newAccessTokenState ? newAccessTokenState.uuid : null,
                });
              };

              useEffect(() => {
                getServiceAccount(id as string).then((serviceAccountData) => {
                  setServiceAccount(serviceAccountData);

                  const promises: Promise<any>[] = [];
                  promises.push(
                    getRoles(serviceAccountData.user).then((rolesData) =>
                      setUserRoles(rolesData["results"])
                    )
                  );

                  if (serviceAccountData?.access_token) {
                    promises.push(
                      getAccessToken(serviceAccountData.access_token as string).then(
                        (serviceAccountData) => setAccessToken(serviceAccountData)
                      )
                    );
                  }

                  Promise.all(promises).then(() => setIsLoading(false));
                });
              }, []);

              return (
                <>
                  <ServiceAccountDetailTopBar
                    serviceAccount={serviceAccount}
                    accessToken={accessToken}
                    setAccessTokenState={setAccessTokenState}
                  />
                  <LoadingSkeleton isLoading={isLoading}>
                    <PageContainer className="margin--top--xl">
                      <ServiceAccountDetail
                        roles={userRoles}
                        serviceAccount={serviceAccount}
                        accessToken={accessToken}
                      />
                    </PageContainer>
                  </LoadingSkeleton>
                </>
              );
            },
          };
        },
      },
    ],
  },
  {
    element: <PermissionedRoutes requiredPermissions={["admin_api.add_serviceaccount"]} />,
    children: [
      {
        path: "service_accounts/:id/update_ip_allowlist/",
        async lazy() {
          const { UpdateIPAllowlist } = await import("./service_accounts/UpdateIPAllowlist.tsx");
          return {
            Component() {
              const { id } = useParams();
              const [isLoading, setIsLoading] = useState(false);

              const [serviceAccount, setServiceAccount] = useState<ServiceAccount>({
                uuid: "",
                username: "Service Account",
                access_token: "",
                user: "",
                ip_allowlist: [],
              });

              useEffect(() => {
                getServiceAccount(id as string).then((serviceAccount) => {
                  setServiceAccount(serviceAccount);
                  setIsLoading(false);
                });
              }, []);
              return (
                <>
                  <TopBar.Compact>
                    <TopBar.Breadcrumb to="/staff">Staff</TopBar.Breadcrumb>
                    <TopBar.Breadcrumb to={`/staff/service_accounts/${serviceAccount.uuid}/`}>
                      {serviceAccount.username}
                    </TopBar.Breadcrumb>
                    <TopBar.Breadcrumb to="">Allowed IPs</TopBar.Breadcrumb>
                  </TopBar.Compact>
                  <LoadingSkeleton isLoading={isLoading}>
                    <PageContainer>
                      <UpdateIPAllowlist serviceAccount={serviceAccount} />
                    </PageContainer>
                  </LoadingSkeleton>
                </>
              );
            },
          };
        },
      },
    ],
  },
  {
    element: <PermissionedRoutes requiredPermissions={["admin_api.add_serviceaccount"]} />,
    children: [
      {
        path: "service_accounts/:id/set_scopes/",
        async lazy() {
          const { TokenWrite } = await import("./service_accounts/TokenWrite.tsx");
          return {
            Component() {
              const { id } = useParams();
              const [isLoading, setIsLoading] = useState(false);
              const [accessToken, setAccessToken] = useState<AccessToken | null>(null);

              const [serviceAccount, setServiceAccount] = useState<ServiceAccount>({
                uuid: "",
                username: "Service Account",
                access_token: "",
                user: "",
                ip_allowlist: [],
              });

              useEffect(() => {
                getServiceAccount(id as string).then((responseData) =>
                  setServiceAccount(responseData)
                );
              }, []);

              useEffect(() => {
                if (serviceAccount?.access_token) {
                  getAccessToken(serviceAccount.access_token as string)
                    .then((responseData) => setAccessToken(responseData))
                    .finally(() => setIsLoading(false));
                  return;
                }
              }, [serviceAccount]);

              return (
                <>
                  <TopBar.Compact>
                    <TopBar.Breadcrumb to="/staff">Staff</TopBar.Breadcrumb>
                    <TopBar.Breadcrumb to={`/staff/service_accounts/${serviceAccount.uuid}/`}>
                      {serviceAccount.username}
                    </TopBar.Breadcrumb>
                    <TopBar.Breadcrumb to="">Scopes</TopBar.Breadcrumb>
                  </TopBar.Compact>
                  <LoadingSkeleton isLoading={isLoading}>
                    <PageContainer>
                      <TokenWrite serviceAccount={serviceAccount} accessToken={accessToken} />
                    </PageContainer>
                  </LoadingSkeleton>
                </>
              );
            },
          };
        },
      },
    ],
  },
  {
    element: <PermissionedRoutes requiredRoles={["narmi"]} />,
    children: [
      {
        path: "add_staff/bulk",
        async lazy() {
          const { AddStaffBulk } = await import("../staff/add_staff_bulk/AddStaffBulk");
          return { element: <AddStaffBulk csrfToken={CSRF_TOKEN} /> };
        },
      },
    ],
  },
];
